import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavbarLayoutMC from '../../../layouts/NavbarLayoutMC';
import WhatsappFlowModelMC from '../../../models/WhatsappFlowModelMC';
import LoadingMC from '../../../components/LoadingMC';
import ButtonMC from '../../../components/ButtonMC';
import TitleMC from '../../../components/TitleMC';
import PaginationMC from '../../../components/PaginationMC';
import IconMC from '../../../components/IconMC';
import { useWhatsappFlowResponses } from '../../../modules/WhatsappFlows/hooks/useWhatsappFlowResponses';
import customHistory from '../../../../customHistory';
import DateRangeMC from '../../../components/DateRangeMC';

const formatResponseValue = (val) => {
  if (val === undefined || val === null || val === '') return null;
  if (Array.isArray(val)) {
    const formatted = val.map((item) => {
      if (item && typeof item === 'object') {
        return item.file_name || item.filename || item.name || (item.id ? String(item.id) : JSON.stringify(item));
      }
      return String(item);
    });
    return formatted.join(', ');
  }
  if (typeof val === 'object') {
    return val.file_name || val.filename || val.name || (val.id ? String(val.id) : JSON.stringify(val));
  }
  return String(val);
};

const WhatsappFlowResponsesScreenContent = ({ flow }) => {
  const {
    t,
    responses,
    loading,
    responseKeys,
    handleDownloadCSV,
    currentPage,
    totalPages,
    handlePageChange,
    dateRangeValue,
    handleDateRangeChange
  } = useWhatsappFlowResponses(flow, "responses");

  const handleBack = () => {
    customHistory.push(`/retailers/${ENV.SLUG}/whatsapp_flows`);
  };

  return (
    <div className="tw-wrapper tw-space-y-4">
      <div>
        <button
          type="button"
          onClick={handleBack}
          className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-gray-500 hover:tw-text-gray-700 tw-font-medium tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
        >
          <IconMC name="arrow-ios-back-outline" size="lg" className="tw-text-gray-500" />
          <span>{t('back', { defaultValue: 'Atrás' })}</span>
        </button>
      </div>

      <div className="tw-flex tw-items-center tw-justify-between">
        <div>
          <TitleMC
            text={flow.name}
            variant="H5"
            textClassName="tw-font-semibold"
            className="!tw-gap-1"
          />
          <p className="tw-text-sm tw-text-gray-500">
            {t('responses_caption')}
          </p>
        </div>

        {responses.length > 0 && (
          <ButtonMC
            text={t('download_csv')}
            onClick={handleDownloadCSV}
            variant="PRIMARY"
            size="small"
            icon="download-outline"
            iconPosition="left"
          />
        )}
      </div>

      {/* Date range filter */}
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-w-64">
          <DateRangeMC
            label={t('period', { defaultValue: 'Período' })}
            name="responses_date_filter"
            defaultDate={dateRangeValue}
            handleChange={handleDateRangeChange}
            useFloatingLabel
          />
        </div>
      </div>

      {loading ? (
        <LoadingMC loading className="tw-py-20" />
      ) : responses.length === 0 ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-py-20">
          <p className="tw-text-sm tw-text-gray-400 tw-italic">
            {t('no_responses_found')}
          </p>
        </div>
      ) : (
        <div className="tw-flex-col tw-space-y-4">
          <div className="tw-overflow-x-auto tw-border tw-border-gray-250 tw-rounded-xl tw-shadow-sm">
            <table className="tw-min-w-full tw-divide-y tw-divide-gray-250 tw-text-left tw-text-sm">
              <thead className="tw-bg-gray-50">
                <tr>
                  <th scope="col" className="tw-px-6 tw-py-3.5 tw-font-bold tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
                    {t('customer')}
                  </th>
                  <th scope="col" className="tw-px-6 tw-py-3.5 tw-font-bold tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
                    {t('contact_label')}
                  </th>
                  <th scope="col" className="tw-px-6 tw-py-3.5 tw-font-bold tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
                    {t('date')}
                  </th>
                  {responseKeys.map((key) => (
                    <th key={key} scope="col" className="tw-px-6 tw-py-3.5 tw-font-bold tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-gray-150 tw-bg-white">
                {responses.map((resp) => {
                  const customerName = resp.customer
                    ? [resp.customer.first_name, resp.customer.last_name].filter(Boolean).join(" ") || resp.customer.whatsapp_name || resp.customer.username || resp.customer.phone || resp.customer.email
                    : t('no_contact');

                  const customerContact = resp.customer
                    ? resp.customer.email || resp.customer.phone
                    : "-";

                  return (
                    <tr key={resp.id} className="hover:tw-bg-gray-50 tw-transition-colors">
                      <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                        <div className="tw-font-semibold tw-text-gray-800">
                          {customerName}
                        </div>
                      </td>
                      <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-gray-500 tw-text-xs">
                        {customerContact}
                      </td>
                      <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-gray-500 tw-text-xs">
                        {resp.created_at ? new Date(resp.created_at).toLocaleString() : ""}
                      </td>
                      {responseKeys.map((key) => {
                        const val = resp.response_json?.[key];
                        const displayVal = formatResponseValue(val);
                        return (
                          <td key={key} className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-gray-700 tw-font-medium">
                            {displayVal === null ? (
                              <span className="tw-text-gray-300">-</span>
                            ) : (
                              displayVal
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="tw-mt-4 tw-flex tw-justify-center">
              <PaginationMC
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const WhatsappFlowResponsesScreenMC = () => {
  const { id } = useParams();
  const { t } = useTranslation('WhatsappFlowsScreenMC');
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        const data = await WhatsappFlowModelMC.getDetail(id);
        setFlow(data);
      } catch (e) {
        console.error("Error fetching flow details", e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchFlow();
  }, [id]);

  return (
    <NavbarLayoutMC>
      <div className="container-fluid-no-padding px-30 py-20 w-100 bg-white store-content">
        {loading ? (
          <LoadingMC loading className="tw-py-20" />
        ) : error || !flow ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-py-20">
            <p className="tw-text-red-500">{t('error_loading')}</p>
          </div>
        ) : (
          <WhatsappFlowResponsesScreenContent flow={flow} />
        )}
      </div>
    </NavbarLayoutMC>
  );
};

export default WhatsappFlowResponsesScreenMC;
